import { useEffect, useState, type CSSProperties } from 'react';
import { IconImageOff } from '../icons';
import { useTranslate } from '../i18n/react';

export interface ThumbnailProps {
  /** A signed URL from the API. Null while the server has not given one yet. */
  url: string | null | undefined;
  alt: string;
  size?: number;
  /** Signed links expire; this is called once, quietly, before giving up. */
  refreshUrl?: () => Promise<string>;
  onOpen?: () => void;
  /**
   * Whether to defer loading until the image is scrolled towards.
   *
   * <p>Worth it in a long table and actively wrong for a photograph somebody has just
   * taken: the browser decides whether a lazy image is needed when it is inserted, and an
   * image inserted into a page that is then never scrolled can stay deferred for ever. The
   * skeleton clears on load, so it shimmers for ever too, and the auditor is left looking
   * at a placeholder where the picture they just took should be.</p>
   */
  lazy?: boolean;
}

type State = 'loading' | 'loaded' | 'error';

/**
 * A photograph behind a link that will stop working.
 *
 * <p>Our links are signed and time-limited — the filesystem store signs them with an HMAC
 * and S3 presigns them — so a thumbnail left on screen while an auditor fills in forty
 * questions will eventually fail to load. One silent refresh handles that; only the second
 * failure is worth telling anyone about.</p>
 *
 * <p>The box keeps its size from the first frame. A list of photographs that reflows as each
 * one arrives moves the thing somebody was about to tap.</p>
 */
export function Thumbnail({ url, alt, size = 40, refreshUrl, onOpen, lazy = true }: ThumbnailProps) {
  const t = useTranslate();
  const [src, setSrc] = useState(url ?? null);
  const [state, setState] = useState<State>(url ? 'loading' : 'error');
  const [refreshed, setRefreshed] = useState(false);

  useEffect(() => {
    setSrc(url ?? null);
    setState(url ? 'loading' : 'error');
    setRefreshed(false);
  }, [url]);

  async function refresh() {
    if (!refreshUrl) {
      setState('error');
      return;
    }

    try {
      setState('loading');
      setSrc(await refreshUrl());
    } catch {
      setState('error');
    }
  }

  const style = { '--thumb-size': `${size}px` } as CSSProperties;

  if (state === 'error') {
    return (
      <span className="nv-thumb is-error" style={style}>
        <button type="button" onClick={() => void refresh()} aria-label={t('image.error')} title={t('image.error')}>
          <IconImageOff size={Math.round(size * 0.45)} />
        </button>
      </span>
    );
  }

  return (
    <span
      className={state === 'loading' ? 'nv-thumb is-loading' : 'nv-thumb'}
      style={style}
      aria-busy={state === 'loading' || undefined}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          width={size}
          height={size}
          loading={lazy ? 'lazy' : 'eager'}
          decoding="async"
          onLoad={() => setState('loaded')}
          onError={() => {
            if (!refreshed && refreshUrl) {
              setRefreshed(true);
              void refresh();
              return;
            }
            setState('error');
          }}
          onClick={onOpen}
          className={onOpen ? 'is-openable' : undefined}
        />
      ) : null}
      {state === 'loading' ? <span className="nv-sr-only">{t('image.loading')}</span> : null}
    </span>
  );
}
